import { useSchedule, type ScheduleSlot } from "@/hooks/useLearning";
import { Link } from "react-router-dom";
import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";

const typeStyles: Record<ScheduleSlot["type"], { label: string; badge: string }> = {
  ders: { label: "Ders", badge: "bg-primary-100 text-primary-700" },
  canli: { label: "Canlı Ders", badge: "bg-red-50 text-red-700" },
  test: { label: "Test", badge: "bg-secondary-100 text-secondary-800" },
};

export default function DersProgramim() {
  const { data: weeklySchedule, loading, error, reload } = useSchedule();
  const today = new Date().toLocaleDateString("tr-TR", { weekday: "long" });

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;

  const schedule = weeklySchedule ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">Ders Programım</h1>
          <p className="text-sm md:text-base text-foreground-600 mt-1">
            Sınıf düzeyin ve çalışma önceliklerine göre hazırlanan haftalık akışın. Bugün: {today}.
          </p>
        </div>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-sm font-semibold whitespace-nowrap">
          <i className="ri-calendar-check-line" aria-hidden="true"></i>
          Bu hafta
        </span>
      </div>

      <div className="rounded-xl border border-primary-200 bg-primary-50 px-4 py-3 flex items-start gap-3 text-sm text-primary-900">
        <i className="ri-sparkling-2-line text-lg text-primary-600 mt-0.5" aria-hidden="true"></i>
        <p>
          <strong>Başlangıç rotan hazır.</strong> Kayıtlı ders saatlerin korunur; boş zamanların seviyene göre konu çalışması ve kısa değerlendirmelerle tamamlanır.
          Her oturumdaki <strong>Başla</strong> düğmesi seni doğrudan ilgili çalışmaya götürür.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {schedule.map((day) => {
          const isToday = day.day.toLowerCase() === today.toLowerCase();
          return (
            <div
              key={day.day}
              className={`rounded-xl border p-5 ${
                isToday
                  ? "bg-primary-50 border-primary-300"
                  : "bg-background-50 border-background-200"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-base text-foreground-950">{day.day}</h3>
                {isToday && (
                  <span className="text-xs font-semibold text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full">
                    Bugün
                  </span>
                )}
              </div>

              {day.slots.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <i className="ri-cup-line text-2xl text-foreground-300" aria-hidden="true"></i>
                  <p className="mt-2 text-sm text-foreground-500">Serbest gün</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {day.slots.map((slot, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 rounded-lg bg-background-50 border border-background-200 px-3 py-2.5"
                    >
                      <span className="text-xs font-semibold text-foreground-500 whitespace-nowrap">
                        {slot.time}
                      </span>
                      <span className="text-sm font-medium text-foreground-800 flex-1">{slot.subject}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${typeStyles[slot.type].badge}`}>
                        {typeStyles[slot.type].label}
                      </span>
                      {slot.href && (
                        <Link
                          to={slot.href}
                          className="text-xs font-bold text-primary-700 hover:text-primary-900 whitespace-nowrap"
                        >
                          Başla
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
